import { useMemo } from "react";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { RoomProduct, RoomInfoProducts } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { HotelRoomRates } from "@tenant/hotels";
import { RadioGroup } from "@tenant/ui";
import clsx from "clsx";
import { useHotelRoomCardRates } from "./hooks";
import defaultStyles from "./HotelRoomCardRates.module.scss";
import { Rewards } from "@/components";
import { calculatePointsEarnedForFiat } from "@/utils/points";

const COMPONENT_KEY = CoreHotelsComponent.HotelRoomCardRates;

export type HotelRoomCardRatesProps = {
  room?: RoomInfoProducts;
  products: Array<RoomProduct>;
  setSelectedProduct: (prduct: RoomProduct) => void;
  selectedProduct: RoomProduct;
  nightCount: number;
};

export const HotelRoomCardRates = ({
  children,
  className,
  ...rest
}: HotelRoomCardRatesProps & PlatformComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { formatFiatCurrency, t } = useI18nContext();

  const { context, handlers, text } = useHotelRoomCardRates(rest);

  const { products } = context;

  const totalPointsEarned = useMemo(() => {
    if (!context.selectedProduct.sellRate?.fiat) {
      return 0;
    }
    return calculatePointsEarnedForFiat(
      context.selectedProduct.sellRate.fiat,
      "hotel"
    );
  }, [context.selectedProduct]);

  const roomRates = useMemo(() => {
    const { room } = rest;

    if (products.length === 0) return null;

    const radioOptions = products
      .filter(
        (product): product is RoomProduct & { rateId: { value: string } } =>
          product.rateId?.value !== undefined
      )
      .map((product) => {
        const rateId = product.rateId.value;
        const priceDiff = room?.pricingDifference?.[rateId] as any;
        const extraAmount =
          priceDiff?.priceDifferenceTaxesAndFeesIncluded?.fiat?.value ?? 0;

        return {
          value: rateId,
          label: (
            <div className={cn("rate-option")} key={rateId}>
              <div className={cn("rate-content")}>
                <HotelRoomRates
                  product={product}
                  className={cn("rate-content-item")}
                />
              </div>
              {extraAmount > 0 &&
                priceDiff?.priceDifferenceTaxesAndFeesIncluded?.fiat && (
                  <div className={cn("extra-amount")}>
                    +
                    {formatFiatCurrency(
                      priceDiff.priceDifferenceTaxesAndFeesIncluded.fiat
                    )}
                  </div>
                )}
            </div>
          ),
          id: rateId,
        };
      });

    return (
      <RadioGroup
        className={cn("rate-radio-group")}
        options={radioOptions}
        defaultValue={context.selectedProduct.rateId?.value}
        onSelectValue={(value) =>
          products.length > 1 && handlers.setSelectedProductWithRateId(value)
        }
      />
    );
  }, [cn, context, handlers, rest, formatFiatCurrency, products]);

  return (
    <div className={clsx(block, className)}>
      {products.length > 1 && (
        <div className={cn("header")}>
          <h4 className={cn("title")}>
            {t("core-hotels.roomSelection.cancellationOptions")}
          </h4>
        </div>
      )}
      {products.length === 1 && (
        <div className={cn("header")}>
          <h4 className={cn("title")}>
            {products[0].cancellationPolicyV2?.CancellationPolicyV2 ===
            "FullyRefundable"
              ? "Refundable"
              : "Non-refundable"}
          </h4>
        </div>
      )}
      <div className={cn("rates")}>{roomRates}</div>
      <div className={cn("bottom-section")}>
        <Rewards
          amount={totalPointsEarned}
          messageKey="core-hotels.roomSelection.earnPoints"
          className={cn("rewards")}
        />
        <div className={cn("price-container")}>
          <div className={cn("room-price")}>{text.selectedPrice}</div>
          <div className={cn("total-price")}>
            {text.totalNotDiscounted && (
              <div className={cn("total-not-discounted")}>
                {text.totalNotDiscounted}
              </div>
            )}
            {text.totalPrice}
          </div>
          <div className={cn("taxes-disclaimer")}>
            {text.taxesAndFeesIncludedDisclaimer}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};
